import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import NotFound from '../../../Images/notFound.png';
import { Paper, makeStyles, TableBody, TableRow, TableCell, CircularProgress } from '@material-ui/core';
import useTable from '../../useTable';
import SearchIcon from '@material-ui/icons/Search';
import CheckUserProvider from '../../../AllHooks/CheckUserProvider';
import { useHistory } from 'react-router-dom';
import CustomAlert from '../../CustomAlert';
import { Helmet } from 'react-helmet';
import { getSupplyOrderDocumentWithUserId } from '../../../Backend/firebase/supply-order';
import ChemistOrderFilter from './ChemistOrderFilter';
import { parseDate } from '../../Models/CommonFunc';
import { DatePickerComponent } from '@syncfusion/ej2-react-calendars';

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        width: '100%'
    },
    fixedHeight: {
        height: '100%',
    },
}));

const headCells = [
    { id: 'ProductName', label: 'Product Name', disableSorting: true },
    { id: 'MedicineType', label: 'Medicine Type', disableSorting: false },
    { id: 'ProductQuantity', label: 'Quantity', disableSorting: false },
    { id: 'ProductScheme', label: 'Scheme', disableSorting: false },
    { id: 'SupplyOrderPostedDateTime', label: 'Date', disableSorting: false },
];

function ChemistOrderHistory() {
    const classes = useStyles();
    const history = useHistory();
    const fixedHeightPaper = clsx(classes.paper, classes.fixedHeight);
    const { userData } = CheckUserProvider();
    const [records, setRecords] = useState([]);
    const [filterFn, setFilterFn] = useState({ fn: items => items });
    const [searchText, setSearchText] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [openNotify, setOpenNotify] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [alertType, setAlertType] = useState('');
    const { TblContainer, TblHead, TblPagination, recordsAfterPagingAndSorting } = useTable(records, headCells, filterFn);

    useEffect(() => {
        const isLogin = localStorage.getItem(process.env.REACT_APP_EMPLOYER_ISLOGIN) || false;
        if (!isLogin || isLogin === "false") {
            history.push("/activity/Login");
        }
    }, []);

    const applyFilters = () => {
        setFilterFn({
            fn: items => {
                return items.filter(item => {
                    const matchesSearch = searchText
                        ? item.ProductName?.toLowerCase().includes(searchText.toLowerCase()) ||
                          item.MedicineType?.toLowerCase().includes(searchText.toLowerCase())
                        : true;
                    const matchesDate = selectedDate
                        ? parseDate(item?.SupplyOrderPostedDateTime).toLocaleString() === parseDate(selectedDate)
                        : true;
                    const matchesStatus = filterStatus
                        ? item.Status === filterStatus
                        : true;
                    return matchesSearch && matchesDate && matchesStatus;
                }).sort((a, b) => a?.ProductName?.localeCompare(b?.ProductName));
            },
        });
    };

    const handleSearchChange = e => {
        setSearchText(e.target.value);
    };

    const handleDateChange = e => {
        setSelectedDate(e.target.value);
    };

    const handleStatusChange = e => {
        setFilterStatus(e.target.value);
    };

    useEffect(() => {
        applyFilters();
    }, [searchText, selectedDate, filterStatus]);

    useEffect(() => {
        if (userData) {
            setIsLoading(true);
            getSupplyOrderDocumentWithUserId(userData.uid)
                .then(data => {
                    setRecords(data?.sort((a, b) => a?.ProductName?.localeCompare(b?.ProductName)));
                    setIsLoading(false);
                })
                .catch(error => {
                    console.error("Error fetching orders:", error);
                    setIsLoading(false);
                });
        }
    }, [userData]);

    const refreshData = async () => {
        if (userData) {
            setIsLoading(true);
            try {
                const getSupplyOrder = await getSupplyOrderDocumentWithUserId(userData.uid);
                setRecords(getSupplyOrder);
                setIsLoading(false);
                setNotificationMessage('Order Record updated successfully.');
                setOpenNotify(true);
                setAlertType('success');
            } catch (error) {
                console.error("Error Occur in Get Order", error);
                setIsLoading(false);
            }
        }
    };

    return (
        <>
            {!userData || isLoading ? (
                <div className="row">
                    <div className="col-md-12 text-center p-4">
                        <CircularProgress />
                    </div>
                </div>
            ) : (
                <div className="allProductsAndSales">
                    <Paper className={fixedHeightPaper}>
                        <Helmet>
                            <meta name="description" content="Find Order you have posted till now." />
                            <title>Order | Kaipsool Pharmatech</title>
                        </Helmet>
                        <div className="row d-flex justify-content-center align-items-center">
                            <div className="col-md-5">
                                <ChemistOrderFilter handleFilters={applyFilters} refreshData={refreshData} />
                            </div>
                            <div className="col-md-2">
                                <div className="allProductsTable__searchBox" style={{ padding: '0px' }}>
                                    <select
                                        className="form-select registerEmp__textbox"
                                        onChange={handleStatusChange}
                                        name="filterStatus"
                                        value={filterStatus}
                                    >
                                        <option value="">Select Status</option>
                                        <option value="returned">Returned</option>
                                    </select>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="allProductsTable__searchBox e-border-outline" style={{ padding: '2px' }}>
                                    <DatePickerComponent
                                        id="selectedDate"
                                        name="selectedDate"
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                        format="dd/MM/yyyy"
                                    />
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="d-flex justify-content-center w-100 align-items-center allProductsTable__searchBox">
                                    <SearchIcon className="mr-2" />
                                    <input
                                        type="text"
                                        placeholder="Search Order..."
                                        className="border-0"
                                        onChange={handleSearchChange}
                                    />
                                </div>
                            </div>
                            {recordsAfterPagingAndSorting().length > 0 ? (
                                <div className="row col-md-12 supply-order-table">
                                    <TblContainer>
                                        <TblHead />
                                        <TableBody>
                                            {recordsAfterPagingAndSorting().map(item => (
                                                <TableRow key={item.id}>
                                                    <TableCell>{item.ProductName || "NA"}</TableCell>
                                                    <TableCell>{item.MedicineType || "Generic"}</TableCell>
                                                    <TableCell>{item.ProductQuantity || "NA"}</TableCell>
                                                    <TableCell>{item.ProductScheme || "NA"}</TableCell>
                                                    <TableCell>
                                                        {typeof item?.SupplyOrderPostedDateTime === "string"
                                                            ? item?.SupplyOrderPostedDateTime
                                                            : parseDate(item?.SupplyOrderPostedDateTime).toLocaleString()}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </TblContainer>
                                    <TblPagination />
                                </div>
                            ) : (
                                <div className="row col-md-12 mt-3 text-center">
                                    <img src={NotFound} className="notFound" alt="No records found" />
                                    <h4>No Order Record Found!</h4>
                                </div>
                            )}
                        </div>
                    </Paper>
                </div>
            )}
            <CustomAlert
                setOpenNotify={setOpenNotify}
                openNotify={openNotify}
                messageType={alertType || "error"}
                notificationMessage={notificationMessage}
            />
        </>
    );
}

export default ChemistOrderHistory;
